/**
 * AppShell — Phase 9B.2.
 *
 * Shell orchestration only. Role-specific navigation lives in
 * `components/nav/*` as pure configuration; this file wires the
 * shell chrome (sidebar, top-bar, mobile drawer, bottom nav) and
 * pulls the right config based on the current user's role.
 *
 * See:
 *   /app/docs/ROSTR_UX_ARCHITECTURE.md §2  — nav by role
 *   /app/memory/PHASE_9_HANDOFF.md §2c    — 9B.2 scope
 *   components/nav/navConfig.js            — grouped rails
 */
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { isHandbookUnread } from "@/lib/handbookVersion";
import { useLocationScope } from "@/lib/locationScope";
import {
  LogOut, MapPin, Menu as MenuIcon, X, Sun, Moon, Languages, ChevronDown,
  Search, Sparkles, Check, Globe2, Bell,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import { toast } from "sonner";
import WelcomeIntro from "@/components/WelcomeIntro";
import ManagerWelcomeModal from "@/components/ManagerWelcomeModal";
import CommandPalette, { useHotkey as useCmdkHotkey } from "@/components/CommandPalette";
import OfflineBanner from "@/components/OfflineBanner";

import { readTheme, saveTheme, applyTheme, effectiveTheme, watchSystemTheme } from "@/lib/theme";
import {
  useRoleCapabilities,
  buildDesktopNav,
  buildMobileBottomNav,
  buildMoreDrawerItems,
  MoreDrawer,
} from "@/components/nav";

/* ================================================================== */
/*  Shell controls (theme / language / notifications)                 */
/* ================================================================== */

// Phase 5 — 3-way theme cycle: light → dark → system → light.
function ThemeToggle() {
  const [pref, setPref] = useState(readTheme);
  const [eff, setEff] = useState(() => effectiveTheme(pref));
  useEffect(() => {
    applyTheme(pref);
    saveTheme(pref);
    setEff(effectiveTheme(pref));
    if (pref !== "system") return;
    return watchSystemTheme((next) => {
      document.documentElement.classList.toggle("dark", next === "dark");
      document.documentElement.dataset.theme = next;
      setEff(next);
    });
  }, [pref]);
  const cycle = () => setPref((p) => (p === "light" ? "dark" : p === "dark" ? "system" : "light"));
  const showDark = eff === "dark";
  return (
    <button
      data-testid="theme-toggle"
      data-theme-pref={pref}
      className="relative h-10 w-10 grid place-items-center rounded-xl border hairline bg-[hsl(var(--surface))] hover:bg-[hsl(var(--muted))] hover:scale-[1.04] active:scale-95 transition-all shadow-[var(--shadow-sm)] focus-visible-ring"
      onClick={cycle}
      aria-label={`Theme: ${pref}`}
      title={`Theme: ${pref}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={pref + "/" + showDark}
          initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
          transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
          className="text-[hsl(var(--text-strong))]"
        >
          {showDark ? <Sun size={17} strokeWidth={2} /> : <Moon size={17} strokeWidth={2} />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

function LanguageMenu() {
  const { lang, setLang, langs } = useI18n();
  const [open, setOpen] = useState(false);
  const current = langs.find((l) => l.code === lang);
  return (
    <div className="relative">
      <button
        data-testid="lang-toggle"
        className="h-10 px-3 gap-1.5 inline-flex items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors text-sm font-medium focus-visible-ring"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Languages size={16} strokeWidth={1.75} />
        <span className="uppercase text-[11px] tracking-wider">{current?.code}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="lang-menu"
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="card-glass absolute right-0 mt-2 w-56 z-40 overflow-hidden shadow-[var(--shadow-xl)]"
            onMouseLeave={() => setOpen(false)}
            role="menu"
          >
            {langs.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors hover:bg-[hsl(var(--muted))] focus-visible-ring ${
                  l.code === lang ? "bg-[hsl(var(--primary-soft))] font-semibold text-[hsl(var(--primary))]" : ""
                }`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                role="menuitem"
              >
                <span className="text-lg">{l.flag}</span>
                <span className={l.code === "ar" ? "ar flex-1" : "flex-1"}>{l.label}</span>
                {l.code === lang && <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NotifBell({ size = 10, testid = "notif-bell" }) {
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const { t } = useI18n();
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try { const { data } = await api.get("/notifications"); if (mounted) setUnread(data.unread || 0); } catch {}
    };
    load(); const timer = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);
  return (
    <button
      data-testid={testid}
      className={`w-${size} h-${size} grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors relative focus-visible-ring`}
      onClick={() => navigate("/notifications")}
      aria-label={t("notifications")}
      title={t("notifications")}
    >
      <Bell size={18} strokeWidth={1.75} />
      {unread > 0 && (
        <motion.span
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          data-testid={`${testid}-badge`}
          className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[hsl(var(--primary))] ring-[3px] ring-[hsl(var(--surface))]"
        />
      )}
    </button>
  );
}

/* ================================================================== */
/*  Nav badges (leave/swap pending counts)                            */
/* ================================================================== */

const BADGE_SEEN_KEY = "rostr.nav.seen";
const BADGE_ROUTE_MAP = { "/requests": "leave", "/swaps": "swap" };

function loadSeen() {
  try { return JSON.parse(localStorage.getItem(BADGE_SEEN_KEY) || "{}") || {}; } catch { return {}; }
}
function persistSeen(map) {
  try { localStorage.setItem(BADGE_SEEN_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

function useNavBadges(isPlanner) {
  const [counts, setCounts] = useState({ leave: 0, swap: 0, time: 0 });
  const [seen, setSeen] = useState(() => loadSeen());
  const location = useLocation();

  useEffect(() => {
    if (!isPlanner) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        if (!mounted) return;
        setCounts({
          leave: data?.pending_leave || 0,
          swap: data?.pending_swaps || 0,
          time: data?.pending_time || 0,
        });
      } catch { /* silent */ }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(timer); };
  }, [isPlanner]);

  useEffect(() => {
    const key = BADGE_ROUTE_MAP[location.pathname];
    if (!key) return;
    const current = counts[key];
    if (typeof current !== "number") return;
    if ((seen[key] || 0) < current) {
      const next = { ...seen, [key]: current };
      setSeen(next);
      persistSeen(next);
    }
  }, [location.pathname, counts, seen]);

  const badgeFor = (to) => {
    const key = BADGE_ROUTE_MAP[to];
    if (!key) return 0;
    return Math.max(0, (counts[key] || 0) - (seen[key] || 0));
  };
  return { badgeFor };
}

function NavBadge({ count, testid }) {
  if (!count) return null;
  return (
    <motion.span
      initial={{ scale: 0 }} animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      data-testid={testid}
      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[hsl(var(--danger))] text-white text-[10px] font-black tabular-nums grid place-items-center shadow-[var(--shadow-sm)]"
    >
      {count > 9 ? "9+" : count}
    </motion.span>
  );
}

/* ================================================================== */
/*  AppShell                                                          */
/* ================================================================== */

export default function AppShell() {
  const { user, logout, legalRequired, legalStatusLoaded } = useAuth();
  const { t } = useI18n();
  const caps = useRoleCapabilities();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [clock, setClock] = useState(() => new Date());
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [documentsEnabled, setDocumentsEnabled] = useState(null);
  useCmdkHotkey(() => setCmdkOpen(true));

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!user?._id) return;
    api.get("/documents/_capability")
      .then((r) => setDocumentsEnabled(!!r.data?.enabled))
      .catch(() => setDocumentsEnabled(false));
  }, [user?._id]);

  const routeLoc = useLocation();
  const navigate = useNavigate();
  const { badgeFor } = useNavBadges(caps.isPlanner);
  const locScope = useLocationScope();

  // Nav config — recomputed whenever the role, documents flag or
  // translation changes. Role changes at login/logout naturally trigger
  // this via caps.role dependency.
  const ctx = { documentsEnabled };
  const navGroups = buildDesktopNav(caps, ctx, t);
  const bnav = buildMobileBottomNav(caps, ctx, t);
  const moreGroups = buildMoreDrawerItems(caps, ctx, t);

  const [handbookNew, setHandbookNew] = useState(() => isHandbookUnread());
  useEffect(() => { setHandbookNew(isHandbookUnread()); }, [routeLoc.pathname]);
  useEffect(() => {
    const on = () => setHandbookNew(isHandbookUnread());
    window.addEventListener("rostr:handbook-viewed", on);
    return () => window.removeEventListener("rostr:handbook-viewed", on);
  }, []);

  // Close drawers on route change.
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    if (moreOpen) setMoreOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLoc.pathname]);

  const handleLogout = async () => { await logout(); toast.success(t("logout")); };

  const brandTo = caps.isPlanner ? "/roster" : "/my-roster";
  const roleSide = caps.isPlanner ? t("manager_side") : t("employee_side");

  return (
    <div className="App min-h-screen md:h-screen md:overflow-hidden flex flex-col md:flex-row bg-[hsl(var(--bg))]">
      {legalStatusLoaded && !legalRequired && (
        <>
          <WelcomeIntro />
          <ManagerWelcomeModal />
        </>
      )}

      {/* ------- Desktop sidebar ------- */}
      <aside
        data-testid="sidebar"
        data-role={caps.role || "anonymous"}
        className="hidden md:flex md:flex-col md:w-[248px] shrink-0 border-r hairline bg-[hsl(var(--surface))] relative"
      >
        <div className="px-5 pt-7 pb-6">
          <NavLink to={brandTo} className="flex items-center gap-2.5 group focus-visible-ring rounded-md" data-testid="sidebar-brand">
            <div className="min-w-0">
              <div className="font-display text-[22px] font-bold leading-none tracking-tight text-[hsl(var(--text-strong))]">Rostr</div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[hsl(var(--text-soft))] mt-1.5">
                {roleSide}
              </div>
            </div>
          </NavLink>
        </div>

        <nav className="flex-1 px-3 pb-4 flex flex-col overflow-y-auto" aria-label={t("navsec_planning")}>
          {navGroups.map((group, gi) => (
            <div key={group.key || group.section || gi} className={gi > 0 ? "mt-5" : ""}>
              {group.section && (
                <div className="px-3.5 mb-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--text-soft))]">
                  {group.section}
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                {group.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={n.testid}
                    className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                  >
                    <span className="nav-icon-bg"><n.icon size={15} strokeWidth={1.85} /></span>
                    <span className="flex-1 truncate">{n.label}</span>
                    {n.handbookNewBadge && handbookNew && (
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white uppercase tracking-wider"
                        data-testid="nav-handbook-new-badge"
                      >{t("nav_new_badge")}</span>
                    )}
                    <NavBadge count={badgeFor(n.to)} testid={`nav-badge-${n.testid}`} />
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User card */}
        <div className="mx-3 mb-4 mt-1 rounded-2xl bg-[hsl(var(--bg))] border hairline p-2.5 flex items-center gap-2.5">
          <NavLink to="/profile" className="flex items-center gap-2.5 flex-1 min-w-0 rounded-xl hover:bg-[hsl(var(--muted))] p-1 -m-1 transition-colors focus-visible-ring" data-testid="sidebar-profile-link">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] grid place-items-center text-white text-[13px] font-semibold shadow-[var(--shadow-sm)] shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-[13px] text-[hsl(var(--text-strong))] leading-tight">{user?.name}</div>
              <div className="text-[10px] text-[hsl(var(--text-muted))] truncate mt-0.5">{t(`role_${user?.role}`)}</div>
            </div>
          </NavLink>
          <button data-testid="logout-button" onClick={handleLogout}
            className="w-8 h-8 grid place-items-center rounded-lg text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--danger))] transition-colors shrink-0 focus-visible-ring"
            aria-label="logout" title={t("logout")}
          >
            <LogOut size={14} strokeWidth={2} />
          </button>
        </div>
      </aside>

      {/* ------- Mobile top bar ------- */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 bg-[hsl(var(--surface))] sticky top-0 z-30 border-b hairline">
        <NavLink to={brandTo} className="font-display text-[20px] font-bold tracking-tight text-[hsl(var(--text-strong))] focus-visible-ring rounded-md px-1" data-testid="mobile-brand">
          Rostr
        </NavLink>
        <div className="flex items-center gap-0.5">
          <NotifBell testid="mobile-notif-bell" />
          <button
            data-testid="mobile-menu-toggle"
            className="w-10 h-10 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors focus-visible-ring"
            onClick={() => setMobileOpen(true)}
            aria-label={t("open_menu")}
          >
            <MenuIcon size={18} strokeWidth={1.75} />
          </button>
        </div>
      </header>

      {/* ------- Mobile side drawer (full nav for planners; personal for employees) ------- */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)} data-testid="mobile-drawer-overlay"
            role="dialog" aria-modal="true"
          >
            <motion.div
              initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: "spring", damping: 32, stiffness: 320 }}
              className="w-[280px] h-full bg-[hsl(var(--surface))] p-5 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--text-soft))]">Rostr</div>
                  <div className="font-display text-[22px] font-bold tracking-tight text-[hsl(var(--text-strong))] mt-0.5">{roleSide}</div>
                </div>
                <button data-testid="mobile-drawer-close" className="w-9 h-9 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] focus-visible-ring" onClick={() => setMobileOpen(false)} aria-label={t("close")}>
                  <X size={16} />
                </button>
              </div>
              <nav className="flex flex-col flex-1 overflow-y-auto" aria-label="mobile navigation">
                {navGroups.map((group, gi) => (
                  <div key={group.key || group.section || gi} className={gi > 0 ? "mt-4" : ""}>
                    {group.section && (
                      <div className="px-3.5 mb-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--text-soft))]">
                        {group.section}
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5">
                      {group.items.map((n) => (
                        <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)} data-testid={`m-${n.testid}`}
                          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                          <span className="nav-icon-bg"><n.icon size={15} strokeWidth={1.85} /></span>
                          <span className="flex-1 truncate">{n.label}</span>
                          {n.handbookNewBadge && handbookNew && (
                            <span
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white uppercase tracking-wider"
                            >{t("nav_new_badge")}</span>
                          )}
                          <NavBadge count={badgeFor(n.to)} testid={`m-nav-badge-${n.testid}`} />
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
              <div className="border-t hairline pt-3 -mx-2 px-2 mt-3">
                <button data-testid="mobile-logout" onClick={handleLogout} className="nav-link w-full mt-1 focus-visible-ring">
                  <span className="nav-icon-bg"><LogOut size={15} strokeWidth={1.75} /></span>
                  {t("logout")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ------- Content ------- */}
      <div data-scroll-container="true" className="flex-1 min-w-0 flex flex-col md:h-screen md:overflow-y-auto">
        {/* Desktop top bar */}
        <div className="hidden md:flex items-center gap-3 px-8 py-3 sticky top-0 z-30 card-glass !rounded-none !border-t-0 !border-l-0 !border-r-0">
          <button
            data-testid="header-command-k"
            onClick={() => setCmdkOpen(true)}
            className="hidden lg:flex items-center gap-2.5 h-10 px-3.5 min-w-[320px] rounded-xl border hairline bg-[hsl(var(--bg))] hover:bg-[hsl(var(--muted))] transition-colors text-left focus-visible-ring"
            aria-label={t("search")}
          >
            <Search size={15} strokeWidth={1.9} className="text-[hsl(var(--text-muted))]" />
            <span className="text-[13px] text-[hsl(var(--text-muted))] flex-1">{t("cmdk_placeholder")}</span>
            <kbd className="hidden xl:inline-flex items-center gap-0.5 h-5 px-1.5 rounded-md border hairline bg-[hsl(var(--surface))] text-[10px] font-mono text-[hsl(var(--text-muted))]">⌘K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-1">
            <div className="hidden xl:flex items-center h-10 px-3 rounded-xl text-[12px] text-[hsl(var(--text-muted))] tabular-nums mr-1" data-testid="header-clock">
              {clock.toLocaleDateString("nl", { weekday: "short", day: "numeric", month: "short" })}
              <span className="mx-1.5 opacity-40">·</span>
              <span className="font-mono font-semibold text-[hsl(var(--text-strong))]">{clock.toLocaleTimeString("nl", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <NotifBell />
            <ThemeToggle />
            <LanguageMenu />
            <div className="w-px h-6 bg-[hsl(var(--border))] mx-2" />
            <NavLink
              to="/profile"
              data-testid="header-profile"
              className="flex items-center gap-2 h-10 pl-1 pr-3 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors focus-visible-ring"
              title={user?.name}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] grid place-items-center text-white text-[12px] font-bold shadow-[var(--shadow-sm)]">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <span className="hidden xl:inline text-[12px] font-semibold text-[hsl(var(--text-strong))]">{t(`role_${user?.role}`)}</span>
            </NavLink>
            <button
              onClick={() => { try { localStorage.removeItem("rostr.whatsnew_seen"); } catch {}; window.dispatchEvent(new Event("rostr:show-whats-new")); }}
              className="hidden xl:grid w-10 h-10 place-items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--text-muted))] focus-visible-ring"
              data-testid="header-whats-new"
              aria-label={t("whats_new")}
              title={t("whats_new")}
            >
              <Sparkles size={15} strokeWidth={1.9} />
            </button>
          </div>
        </div>

        {caps.isOwner && <LocationScopeBar />}

        <main className="flex-1 p-4 md:p-8 lg:p-10 pb-24 md:pb-10 min-h-[calc(100dvh-4rem)]">
          <motion.div
            key={routeLoc.pathname + ":" + (locScope.id || "all")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="w-full"
          >
            <Outlet />
          </motion.div>
        </main>

        {/* ------- Mobile bottom nav (max 5) ------- */}
        {bnav.length > 0 && (
          <div
            className="md:hidden bottom-nav"
            style={{ gridTemplateColumns: `repeat(${bnav.length}, 1fr)` }}
            data-testid="bottom-nav"
            data-role={caps.role || "anonymous"}
            data-count={bnav.length}
            role="navigation"
            aria-label="primary mobile navigation"
          >
            {bnav.map((n) => {
              if (n.to === "__more__") {
                return (
                  <button
                    key="__more__"
                    type="button"
                    data-testid={n.testid}
                    onClick={() => setMoreOpen(true)}
                    aria-label={n.label}
                    className={`bottom-nav-item ${moreOpen ? "active" : ""}`}
                  >
                    <n.icon size={20} strokeWidth={1.85} />
                    <span className="bnav-label">{n.label}</span>
                  </button>
                );
              }
              const badge = badgeFor(n.to);
              return (
                <NavLink
                  key={n.to} to={n.to} data-testid={n.testid}
                  aria-label={n.label}
                  className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}
                >
                  <span className="relative">
                    <n.icon size={20} strokeWidth={1.85} />
                    {badge > 0 && (
                      <motion.span
                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                        data-testid={`bnav-badge-${n.testid}`}
                        className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--danger))] text-white text-[9px] font-black tabular-nums grid place-items-center"
                      >
                        {badge > 9 ? "9+" : badge}
                      </motion.span>
                    )}
                  </span>
                  <span className="bnav-label">{n.label}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>

      {/* ------- More drawer (mobile) ------- */}
      <MoreDrawer open={moreOpen} onClose={() => setMoreOpen(false)} groups={moreGroups} />

      <CommandPalette open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
      <OfflineBanner />
    </div>
  );
}

/* ================================================================== */
/*  Location scope bar (owner-only)                                   */
/* ================================================================== */

function LocationScopeBar() {
  const { t } = useI18n();
  const scope = useLocationScope();
  const [locs, setLocs] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/locations", { params: { __no_scope: true } })
      .then(({ data }) => { if (mounted) setLocs(data || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onChange = () => {
      api.get("/locations", { params: { __no_scope: true } })
        .then(({ data }) => setLocs(data || []))
        .catch(() => {});
    };
    window.addEventListener("rostr:locations-changed", onChange);
    return () => window.removeEventListener("rostr:locations-changed", onChange);
  }, []);

  if (locs.length < 2 && !scope.id) return null;

  const activeLoc = locs.find((l) => l._id === scope.id);
  const label = scope.id
    ? t("loc_scope_pill_one").replace("{name}", scope.name || activeLoc?.name || "")
    : t("loc_scope_pill_all");

  const pick = (loc) => {
    if (!loc) {
      scope.clear();
      toast.success(t("loc_scope_reloading_all"));
    } else {
      scope.setScope(loc._id, loc.name);
      toast.success(t("loc_scope_reloading").replace("{name}", loc.name));
    }
    setOpen(false);
  };

  return (
    <div
      className="w-full px-4 md:px-8 py-2 border-b hairline flex items-center gap-3 relative"
      style={{
        background: scope.id ? "hsl(var(--primary-soft))" : "hsl(var(--muted) / 0.5)",
        color: scope.id ? "hsl(var(--primary))" : "hsl(var(--text-strong))",
      }}
      data-testid="location-scope-bar"
    >
      <div className="flex items-center gap-2 text-[12.5px] font-semibold">
        {scope.id ? <MapPin size={14} strokeWidth={2.4} /> : <Globe2 size={14} strokeWidth={2.2} />}
        <span data-testid="location-scope-label">{label}</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="location-scope-switcher"
          className="h-7 px-3 rounded-full bg-white/80 hover:bg-white text-[11px] font-bold tracking-wide inline-flex items-center gap-1.5 transition-colors focus-visible-ring"
          style={{ color: "hsl(var(--primary))" }}
        >
          {t("loc_scope_switcher_label")}
          <ChevronDown size={11} strokeWidth={2.4} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="card-glass absolute right-4 md:right-8 top-full mt-2 w-[280px] z-50 overflow-hidden shadow-[var(--shadow-xl)] p-1.5"
              data-testid="location-scope-menu"
            >
              <button
                type="button"
                onClick={() => pick(null)}
                data-testid="location-scope-option-all"
                className={`w-full text-left px-3 py-2.5 rounded-xl text-[13px] flex items-center gap-3 transition-colors hover:bg-[hsl(var(--muted))] focus-visible-ring ${
                  !scope.id ? "bg-[hsl(var(--primary-soft))]" : ""
                }`}
              >
                <span className="w-8 h-8 rounded-lg bg-[hsl(var(--muted))] grid place-items-center shrink-0">
                  <Globe2 size={14} strokeWidth={2} />
                </span>
                <span className="flex-1 min-w-0">
                  <div className="font-semibold text-[hsl(var(--text-strong))]">{t("loc_scope_switcher_all")}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">
                    {locs.length}× {t("locations").toLowerCase()}
                  </div>
                </span>
                {!scope.id && <Check size={14} className="text-[hsl(var(--primary))] shrink-0" strokeWidth={2.5} />}
              </button>
              <div className="my-1 border-t hairline" />
              <div className="max-h-[340px] overflow-y-auto">
                {locs.map((l) => (
                  <button
                    key={l._id}
                    type="button"
                    onClick={() => pick(l)}
                    data-testid={`location-scope-option-${l._id}`}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-[13px] flex items-center gap-3 transition-colors hover:bg-[hsl(var(--muted))] focus-visible-ring ${
                      scope.id === l._id ? "bg-[hsl(var(--primary-soft))]" : ""
                    }`}
                  >
                    <span
                      className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
                      style={{ background: (l.color || "#0033A0") + "22" }}
                    >
                      <span className="w-3 h-3 rounded-md" style={{ background: l.color || "#0033A0" }} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <div className="font-semibold text-[hsl(var(--text-strong))] truncate">{l.name}</div>
                      <div className="text-[11px] text-[hsl(var(--text-muted))] truncate mt-0.5">
                        {[l.address, l.city].filter(Boolean).join(", ") || "—"}
                      </div>
                    </span>
                    {scope.id === l._id && <Check size={14} className="text-[hsl(var(--primary))] shrink-0" strokeWidth={2.5} />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
